import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import {
  Business,
  BusinessUpdateRequest,
  BusinessCreateRequest,
} from "../../../../api/src/types";
import { prepareHeaders } from "../utils";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const businessesTagType = "businesses" as const;

export const businessesApi = createApi({
  reducerPath: "businessesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [businessesTagType],
  endpoints: (builder) => ({
    getBusiness: builder.query<Business, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/businesses/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [
        { type: businessesTagType, id: query.id },
      ],
    }),
    getBusinessesByUserId: builder.query<Business[], BasePathParams>({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/businesses`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((business) => ({
          type: businessesTagType,
          id: business.id,
        })) ?? []),
        { type: businessesTagType, id: "LIST" },
      ],
    }),

    updateBusiness: builder.mutation<
      Business,
      BasePathParams & { id: string; data: BusinessUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/businesses/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [{ type: businessesTagType, id }],
    }),

    createBusiness: builder.mutation<
      Business,
      BasePathParams & { data: BusinessCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/businesses`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              businessesApi.util.updateQueryData(
                "getBusinessesByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: Business[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query createBusiness error", error);
        }
      },
    }),
    deleteBusiness: builder.mutation<Business, BasePathParams & { id: string }>(
      {
        query: ({ userId, docId, id }) => ({
          url: `users/${userId}/documents/${docId}/businesses/${id}`,
          method: "DELETE",
        }),
        async onQueryStarted(
          { userId, docId, id },
          { dispatch, queryFulfilled },
        ) {
          await queryFulfilled;
          dispatch(
            businessesApi.util.updateQueryData(
              "getBusinessesByUserId",
              { userId, docId },
              (draft: Business[]) => {
                return draft.filter((item) => item.id !== id);
              },
            ),
          );
        },
      },
    ),
  }),
});

export const {
  useGetBusinessQuery,
  useGetBusinessesByUserIdQuery,
  useUpdateBusinessMutation,
  useCreateBusinessMutation,
  useDeleteBusinessMutation,
} = businessesApi;
